<template>
  <div class="container my-4">
    <TopNav>
      <NavItem
        icon="far fa-save"
        :text="$t('PODCASTS.LINK.SAVE_PODCAST')"
        :pulse="isDirty"
        :loading="inProgress"
        @click="savePodcast"
      />
    </TopNav>
    <div v-if="isLoading" class="w-100 text-center">
      <HMSpinner class="mt-4" />
    </div>
    <div v-else>
      <h1 class="pt-3">{{ podcast.title }}</h1>
      <div class="mt-4">
        <section class="podcast-tabs">
          <HMTabs v-model="selectedTab">
            <HMTab v-for="tab in visibleTabs" :key="tab.name" :name="tab.name">
              {{ tab.title }}
            </HMTab>
          </HMTabs>
        </section>
      </div>
      <div v-if="selectedTab === 'details'">
        <div class="row mt-4">
          <div class="col-12 col-md-6 pr-md-5">
            <h3>{{ $t('PODCASTS.LABELS.TITLE') }}</h3>
            <div class="smaller">
              {{ $t('PODCASTS.TEXT.TITLE_DESCRIPTION') }}
            </div>
          </div>
          <div class="col-12 col-md-6">
            <HMTextInput
              v-model="podcast.title"
              rules="required|max:128"
              name="title"
            />
          </div>
          <div class="col-12">
            <hr />
          </div>
        </div>
        <div class="row mt-4">
          <div class="col-12 col-md-6 pr-md-5">
            <h3>{{ $t('PODCASTS.LABELS.DESCRIPTION') }}</h3>
          </div>
          <div class="col-12 col-md-6">
            <HMTextarea
              v-model="podcast.about_text"
              :maxLength="65535"
              :rows="5"
              :useDynamicHeight="false"
            />
          </div>
          <div class="col-12">
            <hr />
          </div>
        </div>
        <div class="row mt-4">
          <div class="col-12 col-md-6 pr-md-5">
            <h3>{{ $t('PODCASTS.LABELS.HOSTS') }}</h3>
            <div class="smaller">
              {{ $t('PODCASTS.TEXT.HOSTS_DESCRIPTION') }}
            </div>
          </div>
          <div class="col-12 col-md-6">
            <v-select
              :options="allInstructors"
              multiple
              label="name"
              code="id"
              v-model="podcast.instructors"
            />
          </div>
          <div class="col-12">
            <hr />
          </div>
        </div>
        <div class="row mt-4">
          <div class="col-12 col-md-6 pr-md-5">
            <h3>{{ $t('PODCASTS.LABELS.MAKE_THIS_PODCAST_PUBLIC') }}</h3>
            <div class="smaller">
              {{ $t('PODCASTS.TEXT.IS_THIS_PUBLIC_FREE_PODCAST') }}
            </div>
          </div>
          <div class="col-12 col-md-6 w-100 text-right">
            <HMSwitch v-model="podcast.is_free" />
          </div>
          <div class="col-12">
            <hr />
          </div>
        </div>
        <div class="row mt-4" v-if="!podcast.is_free">
          <div class="col-12 col-md-6 pr-md-5">
            <h3>{{ $t('PODCASTS.LABELS.PRODUCTS') }}</h3>
            <div class="smaller">
              {{
                $t(
                  'PODCASTS.TEXT.WHICH_PRODUCTS_SHOULD_HAVE_ACCESS_TO_THIS_PODCAST'
                )
              }}
            </div>
          </div>
          <div class="col-12 col-md-6">
            <v-select
              :options="allActiveTeacherProducts || []"
              label="product_name"
              code="id"
              v-model="podcast.products"
              multiple
            />
          </div>
          <div class="col-12">
            <hr />
          </div>
        </div>
        <div class="row mt-4">
          <div class="col-12 col-md-6 pr-md-5">
            <h3>{{ $t('PODCASTS.LABELS.ARTWORK') }}</h3>
            <div class="smaller">
              {{ $t('PODCASTS.TEXT.ARTWORK_DESCRIPTION') }}
            </div>
          </div>
          <div class="col-12 col-md-6">
            <HMImageUpload
              v-model="podcast.cover_photo"
              photoType="contain"
              canvaDesignType="PodcastCover"
              uploadAspectRatio="1:1"
              aspectRatio="1by1"
              :canvaWidth="3000"
              :canvaHeight="3000"
              type="normal"
              :cropper="false"
            />
          </div>
          <div class="col-12">
            <hr />
          </div>
        </div>
      </div>
      <div v-else-if="selectedTab === 'notifications'">
        <div class="row mt-4">
          <div class="col-12 col-md-6 pr-md-5">
            <h3>{{ $t('PODCASTS.LABELS.LISTENER_NOTIFICATIONS') }}</h3>
            <div class="smaller">
              {{ $t('PODCASTS.TEXT.LISTENER_NOTIFICATIONS_DESCRIPTION') }}
            </div>
          </div>
          <div class="col-12 col-md-6">
            <HMCheckBox v-model="podcast.email_on_new_episodes_published">
              {{ $t('PODCASTS.TEXT.SEND_EMAIL_WHEN_NEW_EPISODES_PUBLISHED') }}
            </HMCheckBox>
          </div>
          <div class="col-12">
            <hr />
          </div>
        </div>
        <div class="row mt-4">
          <div class="col-12 col-md-6 pr-md-5">
            <h3>{{ $t('PODCASTS.LABELS.REPLY_TO_EMAIL') }}</h3>
            <div class="smaller">
              {{ $t('PODCASTS.TEXT.REPLY_TO_EMAIL_DESCRIPTION') }}
            </div>
          </div>
          <div class="col-12 col-md-6">
            <HMTextInput
              v-model="podcast.reply_to_email"
              rules="required|max:255"
              name="reply to email"
            />
          </div>
          <div class="col-12">
            <hr />
          </div>
        </div>
        <div class="row mt-4">
          <div class="col-12 col-md-6 pr-md-5">
            <h3>{{ $t('PODCASTS.LABELS.WELCOME_EMAIL_SUBJECT') }}</h3>
            <div class="smaller">
              {{ $t('PODCASTS.TEXT.WELCOME_EMAIL_SUBJECT_DESCRIPTION') }}
            </div>
          </div>
          <div class="col-12 col-md-6">
            <HMTextInput
              v-model="podcast.email_subject"
              rules="required|max:512"
              name="email subject"
            />
          </div>
          <div class="col-12">
            <hr />
          </div>
        </div>
        <div class="row mt-4">
          <div class="col-12 col-md-6 pr-md-5">
            <h3>{{ $t('PODCASTS.LABELS.WELCOME_EMAIL_MESSAGE') }}</h3>
            <div class="smaller">
              <!--              {{ $t('PODCASTS.TEXT.WELCOME_EMAIL_MESSAGE_DESCRIPTION') }}-->
            </div>
          </div>
          <div class="col-12 col-md-6">
            <div class="form-group d-flex flex-row align-items-center mb-2">
              <HMVueEditor
                id="hm-vue-email-editor"
                class="w-100"
                v-model="podcast.email_content"
                placeholder="Your email content"
              />
            </div>
          </div>
          <div class="col-12">
            <hr />
          </div>
        </div>
      </div>
      <div v-else-if="selectedTab === 'rss'">
        <div class="row mt-4">
          <div
            class="col-12 d-flex flex-row justify-content-start align-items-center"
          >
            <span class="mr-2"
              >Your free podcast RSS feed URL: {{ podcast.feed_url }}</span
            >
            <font-awesome-icon
              :icon="['far', 'link']"
              class="ml-1 copy pointer"
              @click="copyRssLink"
            />
          </div>
        </div>
      </div>
      <div v-else-if="selectedTab === 'distribution'">
        <h2>Distribution</h2>
      </div>
    </div>
  </div>
</template>

<script>
import { cloneDeep } from 'lodash';
import DirtyMixin from '../../../mixins/DirtyMixin';
import { errorToMessage } from '../../../../core/utils/errors';
import TopNav from '@/web/components/navigation/TopNav.vue';
import NavItem from '@/web/components/navigation/NavItem.vue';
import { mapGetters } from 'vuex';
import { formatObjectForBackend } from '../../../../core/api/utils/converter';
import HMVueEditor from '../../../components/common/HMVueEditor';
import HMSwitch from '../../../components/common/HMSwitch';

export default {
  name: 'EditPodcast',
  mixins: [DirtyMixin],
  components: {
    HMSwitch,
    TopNav,
    NavItem,
    HMVueEditor,
  },
  computed: {
    ...mapGetters({
      allInstructors: 'mediaLibrary/instructors',
      allActiveTeacherProducts: 'product/allActiveTeacherProducts',
    }),
    visibleTabs() {
      return this.tabs.filter(tab => !tab.hidden);
    },
    tabs() {
      return [
        {
          name: 'details',
          title: this.$t('PODCASTS.LABELS.DETAILS'),
        },
        {
          name: 'notifications',
          title: this.$t('PODCASTS.LABELS.NOTIFICATIONS'),
          hidden: this.podcast.is_free,
        },
        {
          name: 'rss',
          title: this.$t('PODCASTS.LABELS.RSS_FEED'),
          hidden: !this.podcast.is_free,
        },
        // {
        //   name: 'distribution',
        //   title: this.$t('PODCASTS.LABELS.DISTRIBUTION'),
        // },
      ];
    },
  },
  data() {
    return {
      podcast: null,
      isLoading: true,
      inProgress: false,
      selectedTab: 'details',
    };
  },
  methods: {
    copyRssLink() {
      this.$copyText(this.podcast.feed_url).then(() => {
        this.$notify({
          group: 'app',
          type: 'success',
          text: this.$t('NOTIFICATIONS.TEXT.LINK_COPIED_TO_CLIPBOARD'),
        });
      });
    },
    async loadPodcast() {
      this.isLoading = true;
      try {
        this.podcast = await this.$store.dispatch(
          'podcasts/getPodcast',
          this.$route.params.id
        );
      } catch (error) {
        this.$notify({
          type: 'error',
          text: errorToMessage(error.response, `Request failed: ${error}`),
        });
      } finally {
        this.isLoading = false;
      }
    },
    async savePodcast() {
      this.inProgress = true;
      try {
        const podcastPayload = cloneDeep(this.podcast);
        this.podcast = await this.$store.dispatch(
          'podcasts/editPodcast',
          formatObjectForBackend(podcastPayload)
        );
        this.$notify({
          group: 'app',
          type: 'success',
          title: this.$t('NOTIFICATIONS.TITLE.SUCCESS'),
          text: this.$t('PODCASTS.NOTIFICATIONS.SUCCESSFULLY_UPDATED'),
        });
        this.$nextTick(() => {
          this.clearDirty();
        });
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: 'Failed to save podcast',
          text: errorToMessage(error.response, `Request failed: ${error}`),
        });
      } finally {
        this.inProgress = false;
      }
    },
  },
  async mounted() {
    this.$store.dispatch('mediaLibrary/getInstructors');
    this.$store.dispatch('product/getTeacherProducts');
    await this.loadPodcast();
  },
  watch: {
    podcast: {
      handler(newPodcast, oldPodcast) {
        if (oldPodcast) {
          this.setDirty();
        }
      },
      deep: true,
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/web/styles/components-variables.scss';
</style>
